'use strict';

class LightboxThumbsV2 {
  constructor() {
    this.overlay = null;
    this.items = [];
    this._modelViewerLoaded = false;
    this.currentIndex = 0;
    this.isOpen = false;
    this.built = false;
    // Zoom state
    this.scale = 1;
    this.minScale = 1;
    this.maxScale = 5;
    this.panX = 0;
    this.panY = 0;
    // Mouse drag state
    this.isMouseDragging = false;
    this.mouseDownPos = null;
    this.mouseDragAxis = null; // 'x' or 'y'
    this.mouseDragStartX = 0;
    this.mouseDragStartY = 0;
    this.mouseDragBaseX = 0;
    this.mouseDragBaseY = 0;
    // Touch state
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchDeltaX = 0;
    this.touchDeltaY = 0;
    this.touchAxis = null;
    this.isTouching = false;
    // Pinch state
    this.pinchStartDist = 0;
    this.pinchStartScale = 1;
    this.isPinching = false;
    this.enableColorAdjustments = false;
    this.adjustCollapsed = true;
    this.adjustI18n = {
      title: 'TrueImage\u2122',
      subtitle: 'See how this product looks under different lighting conditions.',
      open: 'TrueImage\u2122',
      close: 'TrueImage\u2122 ausblenden',
      toggle: 'TrueImage\u2122',
      brightness: 'Brightness',
      contrast: 'Contrast',
      saturation: 'Saturation',
      warmth: 'Warmth',
      sharpness: 'Sharpness',
      reset: 'Reset',
      scene_label: 'Lighting',
      scene_daylight: '\u2600\uFE0F Daylight',
      scene_office: '\uD83D\uDCA1 Office',
      scene_evening: '\uD83C\uDF19 Evening',
      scene_flash: '\uD83D\uDCF8 Flash',
      color_label: 'Color',
      preset_neutral: 'Neutral',
      preset_brighter: 'Brighter',
      preset_softer: 'Softer',
      preset_richer: 'Richer',
      view_label: 'View',
      grayscale: 'B&W',
      invert: 'Invert',
      pipette: '\uD83C\uDFA8 Color Picker',
      splitview: '\u25D0 Split View',
      note: 'Tip: Colors can appear different depending on display settings and ambient light.'
    };
    this.adjust = {
      brightness: 100,
      contrast: 100,
      saturation: 100,
      warmth: 0,
      sharpness: 0,
      grayscale: false,
      invert: false,
      preset: 'neutral'
    };
    this.adjustPresets = {
      neutral: { brightness: 100, contrast: 100, saturation: 100, warmth: 0, sharpness: 0 },
      brighter: { brightness: 108, contrast: 100, saturation: 100, warmth: 0, sharpness: 0 },
      softer: { brightness: 100, contrast: 95, saturation: 100, warmth: 0, sharpness: 0 },
      richer: { brightness: 100, contrast: 102, saturation: 110, warmth: 0, sharpness: 0 }
    };
    this.scenePresets = {
      daylight: { brightness: 102, contrast: 100, saturation: 100, warmth: -5, sharpness: 0 },
      office: { brightness: 105, contrast: 98, saturation: 95, warmth: -10, sharpness: 0 },
      evening: { brightness: 98, contrast: 100, saturation: 100, warmth: 25, sharpness: 0 },
      flash: { brightness: 112, contrast: 108, saturation: 95, warmth: -8, sharpness: 2 }
    };
    this._pipetteActive = false;
    this._splitViewActive = false;
    this._splitPosition = 50;
  }

  init(zoomActionEl) {
    this.zoomActionEl = zoomActionEl;
    const root = this.zoomActionEl?.closest('.sec__featured-product');
    this.enableColorAdjustments = root?.dataset?.lightboxColorAdjustments === 'true';
    const i18nRaw = root?.getAttribute('data-lightbox-adjust-i18n');
    if (i18nRaw) {
      try {
        const parsed = JSON.parse(i18nRaw);
        this.adjustI18n = { ...this.adjustI18n, ...parsed };
      } catch (_) {}
    }
    this.bindGalleryClicks();
  }

  bindGalleryClicks() {
    if (!this.zoomActionEl) return;
    // Use event delegation so dynamically re-rendered slides (swiper) still work
    // Only open lightbox for main images (with data-position), NOT thumbnails
    this.zoomActionEl.addEventListener('click', (e) => {
      const el = e.target.closest('[data-media-type][data-position]') || e.target.closest('a.media-gallery__image[data-position]');
      if (!el) return;
      // Ignore clicks on elements inside the thumbnail strip
      if (el.closest('.thumbnail-slide') || el.closest('.media-thumb-swiper')) return;
      e.preventDefault();
      e.stopPropagation();
      this.collectMedia();
      const posAttr = el.getAttribute('data-position');
      let position = posAttr ? parseInt(posAttr, 10) - 1 : 0;
      if (position < 0) position = 0;
      if (position >= this.items.length) position = 0;
      this.open(position);
    });
  }

  // Strip Shopify CDN size params to get the original full-resolution URL
  _shopifyCdnOriginal(url) {
    if (!url || !url.includes('cdn.shopify.com')) return url;
    try {
      const u = new URL(url);
      u.searchParams.delete('width');
      u.searchParams.delete('height');
      u.searchParams.delete('crop');
      return u.toString();
    } catch { return url; }
  }

  // Set a specific width on a Shopify CDN URL
  _shopifyCdnSized(url, width) {
    if (!url || !url.includes('cdn.shopify.com')) return url;
    try {
      const u = new URL(url);
      u.searchParams.set('width', String(width));
      u.searchParams.delete('height');
      u.searchParams.delete('crop');
      return u.toString();
    } catch { return url; }
  }

  collectMedia() {
    this.items = [];
    if (!this.zoomActionEl) return;
    const els = this.zoomActionEl.querySelectorAll('[data-media-type][data-position]');
    // Exclude thumbnail strip duplicates
    const filtered = [...els].filter(el => !el.closest('.media-thumb-swiper') && !el.closest('.thumbnail-slide'));
    const sorted = filtered.sort((a, b) => parseInt(a.dataset.position) - parseInt(b.dataset.position));
    sorted.forEach((el) => {
      const type = el.dataset.mediaType;
      const poster = el.dataset.poster || '';
      const position = parseInt(el.dataset.position, 10);
      const base = { type, position, thumbSrc: poster };
      switch (type) {
        case 'image': {
          const img = el.querySelector('img');
          // Priority: href (usually full-res) > largest srcset entry > img.src (may be mobile-sized)
          let rawUrl = el.getAttribute('href') || '';
          if (!rawUrl && img) {
            const srcset = img.getAttribute('srcset') || img.getAttribute('data-srcset') || '';
            if (srcset) {
              // Pick the largest srcset entry
              const entries = srcset.split(',').map(s => s.trim()).filter(Boolean);
              let best = '', bestW = 0;
              for (const entry of entries) {
                const parts = entry.split(/\s+/);
                const w = parseInt(parts[1], 10) || 0;
                if (w > bestW) { bestW = w; best = parts[0]; }
              }
              if (best) rawUrl = best;
            }
            if (!rawUrl) rawUrl = img.src || '';
          }
          if (!rawUrl) return;
          // Always strip size params for the main lightbox image → full resolution
          const fullSrc = this._shopifyCdnOriginal(rawUrl);
          const w = parseInt(el.dataset.pswpWidth, 10) || 1200;
          const h = parseInt(el.dataset.pswpHeight, 10) || 1200;
          const alt = img?.alt || '';
          const thumbSrc = this._shopifyCdnSized(fullSrc, 200);
          this.items.push({ ...base, src: fullSrc, thumbSrc, width: w, height: h, alt });
          break;
        }
        case 'video':
          this.items.push({ ...base, videoSrc: el.dataset.videoSrc || '' });
          break;
        case 'external_video':
          this.items.push({ ...base, videoHost: el.dataset.videoHost || '', videoId: el.dataset.videoId || '' });
          break;
        case 'model': {
          const modelEl = el.querySelector('product-model template');
          let modelSrc = '';
          if (modelEl) {
            const tmp = document.createElement('div');
            tmp.innerHTML = modelEl.innerHTML;
            const mv = tmp.querySelector('model-viewer');
            if (mv) modelSrc = mv.getAttribute('src') || '';
          }
          this.items.push({ ...base, mediaId: el.dataset.mediaId || '', modelSrc });
          break;
        }
      }
    });
  }

  build() {
    if (this.built) return;
    this.built = true;

    const adjustToggleMarkup = this.enableColorAdjustments
      ? `<button type="button" class="lbt-adjust-toggle" aria-expanded="false" aria-label="${this.adjustI18n.open}">
            <svg viewBox="0 0 24 24" fill="none"><line x1="5" y1="7" x2="19" y2="7"/><circle cx="9" cy="7" r="2"/><line x1="5" y1="12" x2="19" y2="12"/><circle cx="15" cy="12" r="2"/><line x1="5" y1="17" x2="19" y2="17"/><circle cx="11" cy="17" r="2"/></svg>
            <span class="lbt-adjust-toggle-label">${this.adjustI18n.toggle}</span>
          </button>`
      : '';

    const adjustPanelMarkup = this.enableColorAdjustments
      ? `<div class="lbt-adjust-panel" role="group" aria-label="${this.adjustI18n.title}" style="display:none">
            <div class="lbt-adjust-panel-header">
              <span class="lbt-adjust-panel-title">${this.adjustI18n.title}</span>
              <button type="button" class="lbt-adjust-panel-close" aria-label="${this.adjustI18n.close}">
                <svg viewBox="0 0 24 24" fill="none"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <p class="lbt-adjust-subtitle">${this.adjustI18n.subtitle}</p>

            <div class="lbt-adjust-section-label">${this.adjustI18n.scene_label}</div>
            <div class="lbt-adjust-presets lbt-adjust-scenes" role="group" aria-label="Scene presets">
              <button type="button" class="lbt-adjust-preset lbt-adjust-scene" data-scene="daylight">${this.adjustI18n.scene_daylight}</button>
              <button type="button" class="lbt-adjust-preset lbt-adjust-scene" data-scene="office">${this.adjustI18n.scene_office}</button>
              <button type="button" class="lbt-adjust-preset lbt-adjust-scene" data-scene="evening">${this.adjustI18n.scene_evening}</button>
              <button type="button" class="lbt-adjust-preset lbt-adjust-scene" data-scene="flash">${this.adjustI18n.scene_flash}</button>
            </div>

            <div class="lbt-adjust-section-label">${this.adjustI18n.color_label}</div>
            <div class="lbt-adjust-presets" role="group" aria-label="Color presets">
              <button type="button" class="lbt-adjust-preset is-active" data-preset="neutral">${this.adjustI18n.preset_neutral}</button>
              <button type="button" class="lbt-adjust-preset" data-preset="brighter">${this.adjustI18n.preset_brighter}</button>
              <button type="button" class="lbt-adjust-preset" data-preset="softer">${this.adjustI18n.preset_softer}</button>
              <button type="button" class="lbt-adjust-preset" data-preset="richer">${this.adjustI18n.preset_richer}</button>
            </div>

            <div class="lbt-adjust-row lbt-adjust-row-brightness">
              <label class="lbt-adjust-label" for="lbt-adjust-brightness">${this.adjustI18n.brightness}</label>
              <input id="lbt-adjust-brightness" type="range" class="lbt-adjust-slider lbt-adjust-brightness" min="80" max="120" value="100" step="1">
              <span class="lbt-adjust-value lbt-adjust-brightness-value">100%</span>
            </div>
            <div class="lbt-adjust-row lbt-adjust-row-contrast">
              <label class="lbt-adjust-label" for="lbt-adjust-contrast">${this.adjustI18n.contrast}</label>
              <input id="lbt-adjust-contrast" type="range" class="lbt-adjust-slider lbt-adjust-contrast" min="85" max="115" value="100" step="1">
              <span class="lbt-adjust-value lbt-adjust-contrast-value">100%</span>
            </div>
            <div class="lbt-adjust-row lbt-adjust-row-saturation">
              <label class="lbt-adjust-label" for="lbt-adjust-saturation">${this.adjustI18n.saturation}</label>
              <input id="lbt-adjust-saturation" type="range" class="lbt-adjust-slider lbt-adjust-saturation" min="85" max="120" value="100" step="1">
              <span class="lbt-adjust-value lbt-adjust-saturation-value">100%</span>
            </div>
            <div class="lbt-adjust-row lbt-adjust-row-warmth">
              <label class="lbt-adjust-label" for="lbt-adjust-warmth">${this.adjustI18n.warmth}</label>
              <input id="lbt-adjust-warmth" type="range" class="lbt-adjust-slider lbt-adjust-warmth" min="-30" max="30" value="0" step="1">
              <span class="lbt-adjust-value lbt-adjust-warmth-value">0</span>
            </div>
            <div class="lbt-adjust-row lbt-adjust-row-sharpness">
              <label class="lbt-adjust-label" for="lbt-adjust-sharpness">${this.adjustI18n.sharpness}</label>
              <input id="lbt-adjust-sharpness" type="range" class="lbt-adjust-slider lbt-adjust-sharpness" min="0" max="10" value="0" step="1">
              <span class="lbt-adjust-value lbt-adjust-sharpness-value">0</span>
            </div>

            <div class="lbt-adjust-section-label">${this.adjustI18n.view_label}</div>
            <div class="lbt-adjust-toggles">
              <button type="button" class="lbt-adjust-toggle-btn lbt-adjust-grayscale-btn">${this.adjustI18n.grayscale}</button>
              <button type="button" class="lbt-adjust-toggle-btn lbt-adjust-invert-btn">${this.adjustI18n.invert}</button>
              <button type="button" class="lbt-adjust-toggle-btn lbt-adjust-pipette-btn">${this.adjustI18n.pipette}</button>
              <button type="button" class="lbt-adjust-toggle-btn lbt-adjust-splitview-btn">${this.adjustI18n.splitview}</button>
            </div>

            <div class="lbt-adjust-footer">
              <button type="button" class="lbt-adjust-reset">${this.adjustI18n.reset}</button>
              <small class="lbt-adjust-note">${this.adjustI18n.note}</small>
            </div>
          </div>`
      : '';

    const adjustViewportExtras = this.enableColorAdjustments
      ? `<canvas class="lbt-pipette-canvas" style="display:none"></canvas>
          <div class="lbt-pipette-tooltip" style="display:none"></div>
          <div class="lbt-splitview-handle" style="display:none"><div class="lbt-splitview-line"></div></div>
          <img class="lbt-splitview-original" style="display:none" draggable="false" alt="">`
      : '';

    this.overlay = document.createElement('div');
    this.overlay.className = 'lbt-overlay';
    this.overlay.innerHTML = `
      <button class="lbt-close" aria-label="Close">
        <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
      <div class="lbt-wrapper">
        <div class="lbt-thumbs">
          <button class="lbt-thumb-nav lbt-thumb-nav-up" aria-label="Scroll thumbnails up">
            <svg viewBox="0 0 24 24"><polyline points="18 15 12 9 6 15"/></svg>
          </button>
          <div class="lbt-thumbs-scroll"></div>
          <button class="lbt-thumb-nav lbt-thumb-nav-down" aria-label="Scroll thumbnails down">
            <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9"/></svg>
          </button>
        </div>
        ${adjustToggleMarkup}
        ${adjustPanelMarkup}
        <div class="lbt-main">
          <button class="lbt-arrow lbt-arrow-prev" aria-label="Previous">
            <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div class="lbt-viewport">
            <div class="lbt-img-wrap">
              <img class="lbt-img" src="" alt="" draggable="false">
            </div>
            ${adjustViewportExtras}
          </div>
          <button class="lbt-arrow lbt-arrow-next" aria-label="Next">
            <svg viewBox="0 0 24 24"><polyline points="9 6 15 12 9 18"/></svg>
          </button>
          <div class="lbt-counter"></div>
          <div class="lbt-zoom-controls">
            <button class="lbt-zoom-btn lbt-zoom-out" aria-label="Zoom out">
              <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            <input type="range" class="lbt-zoom-slider" min="100" max="500" value="100" step="10">
            <button class="lbt-zoom-btn lbt-zoom-in" aria-label="Zoom in">
              <svg viewBox="0 0 24 24"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            </button>
            <span class="lbt-zoom-label">100%</span>
          </div>
        </div>
        <div class="lbt-mobile-thumbs">
          <div class="lbt-mobile-thumbs-scroll"></div>
        </div>
      </div>
    `;
    document.body.appendChild(this.overlay);

    this.viewport = this.overlay.querySelector('.lbt-viewport');
    this.imgWrap = this.overlay.querySelector('.lbt-img-wrap');
    this.mainImg = this.overlay.querySelector('.lbt-img');
    this.thumbsScroll = this.overlay.querySelector('.lbt-thumbs-scroll');
    this.mobileThumbsScroll = this.overlay.querySelector('.lbt-mobile-thumbs-scroll');
    this.counter = this.overlay.querySelector('.lbt-counter');
    this.mainArea = this.overlay.querySelector('.lbt-main');
    this.zoomSlider = this.overlay.querySelector('.lbt-zoom-slider');
    this.zoomLabel = this.overlay.querySelector('.lbt-zoom-label');
    this.adjustToggleButton = this.overlay.querySelector('.lbt-adjust-toggle');
    this.adjustToggleLabel = this.overlay.querySelector('.lbt-adjust-toggle-label');
    this.adjustPanelCloseButton = this.overlay.querySelector('.lbt-adjust-panel-close');
    this.adjustPanel = this.overlay.querySelector('.lbt-adjust-panel');
    this.adjustControls = this.adjustPanel;

    if (this.enableColorAdjustments) {
      this.bindAdjustEvents();
      this.resetAdjustments({ hard: true });
      this.setAdjustCollapsed(true);
    }

    // Close
    this.overlay.querySelector('.lbt-close').addEventListener('click', () => this.close());
    // Arrows
    this.overlay.querySelector('.lbt-arrow-prev').addEventListener('click', () => this.prev());
    this.overlay.querySelector('.lbt-arrow-next').addEventListener('click', () => this.next());
    // Thumb scroll buttons
    this.overlay.querySelector('.lbt-thumb-nav-up').addEventListener('click', () => {
      this.thumbsScroll.scrollBy({ top: -200, behavior: 'smooth' });
    });
    this.overlay.querySelector('.lbt-thumb-nav-down').addEventListener('click', () => {
      this.thumbsScroll.scrollBy({ top: 200, behavior: 'smooth' });
    });
    // Zoom controls
    this.overlay.querySelector('.lbt-zoom-in').addEventListener('click', () => this.zoomIn());
    this.overlay.querySelector('.lbt-zoom-out').addEventListener('click', () => this.zoomOut());
    this.zoomSlider.addEventListener('input', () => {
      const val = parseInt(this.zoomSlider.value, 10);
      this.setScale(val / 100);
    });

    // Keyboard
    document.addEventListener('keydown', (e) => {
      if (!this.isOpen) return;
      if (e.key === 'Escape') this.close();
      if (e.key === 'ArrowLeft') this.prev();
      if (e.key === 'ArrowRight') this.next();
      if (this._isImageType(this.items[this.currentIndex])) {
        if (e.key === '+' || e.key === '=') this.zoomIn();
        if (e.key === '-') this.zoomOut();
      }
    });

    // Scroll wheel: zoom only for images
    this.viewport.addEventListener('wheel', (e) => {
      if (!this.isOpen) return;
      if (this._pipetteActive) return;
      if (!this._isImageType(this.items[this.currentIndex])) return;
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.2 : 0.2;
      this.zoomBy(delta, e.clientX, e.clientY);
    }, { passive: false });

    // Mouse: click image to toggle zoom, click empty space to close, drag to pan/navigate
    const onMouseDown = (e) => {
      if (!this.isOpen) return;
      if (this._pipetteActive) return;
      if (e.button !== 0) return;
      e.preventDefault();
      this.mouseDownTarget = e.target;
      this.isMouseDragging = true;
      this.mouseDragStartX = e.clientX;
      this.mouseDragStartY = e.clientY;
      this.mouseDragBaseX = this.panX;
      this.mouseDragBaseY = this.panY;
    };
    this.viewport.addEventListener('mousedown', onMouseDown);
    this.mainImg.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', (e) => {
      if (!this.isMouseDragging) return;
      const dx = e.clientX - this.mouseDragStartX;
      const dy = e.clientY - this.mouseDragStartY;
      if (this.scale > 1 && this.mouseDownTarget === this.mainImg) {
        // When zoomed and dragging on image: pan in ALL directions
        this.panX = this.mouseDragBaseX + dx;
        this.panY = this.mouseDragBaseY + dy;
        this.clampPan();
        this.applyTransform();
      }
    });
    window.addEventListener('mouseup', (e) => {
      if (!this.isMouseDragging) return;
      this.isMouseDragging = false;
      if (this._pipetteActive) { this.mouseDownTarget = null; return; }
      const dx = e.clientX - this.mouseDragStartX;
      const dy = e.clientY - this.mouseDragStartY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < 6) {
        // Click (no drag)
        if (this.mouseDownTarget === this.mainImg) {
          // Click on image: toggle zoom
          if (this.scale > 1) {
            this.resetZoom();
          } else {
            this.zoomToFitWidth();
          }
        } else {
          // Click on empty black space: close lightbox
          this.close();
        }
      } else if (this.scale <= 1 && this.mouseDownTarget === this.mainImg) {
        // At 1x: horizontal drag on image = navigate
        if (dx < -60) this.next();
        else if (dx > 60) this.prev();
      }
      this.mouseDownTarget = null;
    });

    // Touch events
    this.viewport.addEventListener('touchstart', (e) => this.onTouchStart(e), { passive: false });
    this.viewport.addEventListener('touchmove', (e) => this.onTouchMove(e), { passive: false });
    this.viewport.addEventListener('touchend', (e) => this.onTouchEnd(e), { passive: true });
    this.viewport.addEventListener('touchcancel', () => this.onTouchCancel(), { passive: true });
  }

  populateThumbs() {
    const total = this.items.length;
    if (total === 0) return;

    const playSvg = '<svg class="lbt-thumb-overlay" viewBox="0 0 24 24" fill="white"><polygon points="8,5 19,12 8,19"/></svg>';
    const modelSvg = '<svg class="lbt-thumb-overlay" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="1.5"><path d="M12 3L21 8v8l-9 5-9-5V8l9-5z"/><path d="M12 12l9-5M12 12v9M12 12L3 8"/></svg>';

    let thumbHtml = '';
    for (let i = 0; i < total; i++) {
      const item = this.items[i];
      const src = item.thumbSrc || item.src || '';
      const alt = item.alt || '';
      let overlay = '';
      if (item.type === 'video' || item.type === 'external_video') overlay = playSvg;
      else if (item.type === 'model') overlay = modelSvg;
      thumbHtml += `<div class="lbt-thumb" data-index="${i}"><img src="${src}" alt="${alt}" loading="lazy">${overlay}</div>`;
    }
    this.thumbsScroll.innerHTML = thumbHtml;
    this.mobileThumbsScroll.innerHTML = thumbHtml;

    this.thumbs = this.thumbsScroll.querySelectorAll('.lbt-thumb');
    this.mobileThumbsList = this.mobileThumbsScroll.querySelectorAll('.lbt-thumb');

    const bindThumbClick = (thumb) => {
      thumb.addEventListener('click', () => {
        const idx = parseInt(thumb.getAttribute('data-index'), 10);
        this.resetZoom();
        this.goTo(idx);
      });
    };
    this.thumbs.forEach(bindThumbClick);
    this.mobileThumbsList.forEach(bindThumbClick);
  }

  // Clean up any active video/model content before switching slides
  _clearMediaContent() {
    if (!this.imgWrap) return;
    // Pause any playing video
    const video = this.imgWrap.querySelector('video');
    if (video) { video.pause(); video.src = ''; }
    // Remove iframes (YouTube/Vimeo)
    const iframe = this.imgWrap.querySelector('iframe');
    if (iframe) iframe.remove();
    // Remove model-viewer
    const mv = this.imgWrap.querySelector('model-viewer');
    if (mv) mv.remove();
    // Remove video wrappers
    const vw = this.imgWrap.querySelector('.lbt-video-wrap');
    if (vw) vw.remove();
    // Remove model wrappers
    const mw = this.imgWrap.querySelector('.lbt-model-wrap');
    if (mw) mw.remove();
    // Restore the main img element if it was hidden
    if (this.mainImg) {
      this.mainImg.style.display = '';
      this.mainImg.src = '';
      this.mainImg.alt = '';
    }
  }

  _isImageType(item) {
    return !item || item.type === 'image';
  }

  showItem(index) {
    if (this.items.length === 0) return;
    this._clearMediaContent();
    const item = this.items[index];
    if (!item) return;

    const zoomControls = this.overlay?.querySelector('.lbt-zoom-controls');
    this.toggleAdjustControlsForMedia(item);

    switch (item.type) {
      case 'video': {
        if (this.mainImg) this.mainImg.style.display = 'none';
        if (zoomControls) zoomControls.style.display = 'none';
        const wrap = document.createElement('div');
        wrap.className = 'lbt-video-wrap';
        const vid = document.createElement('video');
        vid.controls = true;
        vid.autoplay = true;
        vid.muted = true;
        vid.playsInline = true;
        vid.preload = 'metadata';
        vid.poster = item.thumbSrc || '';
        vid.src = item.videoSrc || '';
        vid.setAttribute('aria-label', 'Product video');
        wrap.appendChild(vid);
        this.imgWrap.appendChild(wrap);
        break;
      }
      case 'external_video': {
        if (this.mainImg) this.mainImg.style.display = 'none';
        if (zoomControls) zoomControls.style.display = 'none';
        const wrap = document.createElement('div');
        wrap.className = 'lbt-video-wrap';
        const iframe = document.createElement('iframe');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('allow', 'autoplay; encrypted-media');
        iframe.setAttribute('frameborder', '0');
        iframe.setAttribute('aria-label', 'Product video');
        if (item.videoHost === 'youtube') {
          iframe.src = `https://www.youtube.com/embed/${item.videoId}?autoplay=1&mute=1&rel=0&enablejsapi=1`;
        } else {
          iframe.src = `https://player.vimeo.com/video/${item.videoId}?autoplay=1&muted=1`;
        }
        wrap.appendChild(iframe);
        this.imgWrap.appendChild(wrap);
        break;
      }
      case 'model': {
        if (this.mainImg) this.mainImg.style.display = 'none';
        if (zoomControls) zoomControls.style.display = 'none';
        const wrap = document.createElement('div');
        wrap.className = 'lbt-model-wrap';
        if (item.modelSrc) {
          const mv = document.createElement('model-viewer');
          mv.setAttribute('src', item.modelSrc);
          mv.setAttribute('camera-controls', '');
          mv.setAttribute('auto-rotate', '');
          mv.setAttribute('ar', '');
          mv.setAttribute('ar-modes', 'webxr scene-viewer quick-look');
          mv.setAttribute('poster', item.thumbSrc || '');
          mv.setAttribute('aria-label', '3D product model');
          mv.setAttribute('style', 'width:100%;height:100%;');
          wrap.appendChild(mv);
          // Lazy-load model-viewer-ui if not already loaded
          if (!this._modelViewerLoaded && typeof Shopify !== 'undefined' && Shopify.loadFeatures) {
            this._modelViewerLoaded = true;
            Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0' }]);
          }
        } else {
          // Fallback: show poster image with message
          wrap.innerHTML = `<img src="${item.thumbSrc || ''}" alt="3D model" style="max-width:100%;max-height:100%;object-fit:contain;"><p style="color:#fff;text-align:center;margin-top:12px;">3D model preview</p>`;
        }
        this.imgWrap.appendChild(wrap);
        break;
      }
      default: {
        // Image type (default)
        if (zoomControls) zoomControls.style.display = '';
        if (this.mainImg) {
          this.mainImg.style.display = '';
          this.mainImg.src = item.src || '';
          this.mainImg.alt = item.alt || '';
        }
        // Update split-view original image when switching images while split-view is active
        if (this._splitViewActive && this._splitViewOriginal) {
          this._splitViewOriginal.src = item.src || '';
          this._splitViewOriginal.style.filter = 'none';
          this._applySplitPosition();
        }
        this.applyAdjustments();
        break;
      }
    }
  }

  open(index) {
    this.build();
    this.populateThumbs();
    if (this.items.length === 0) return;

    this.currentIndex = index || 0;
    this.isOpen = true;
    this.overlay.classList.add('lbt-open');
    document.body.style.overflow = 'hidden';
    this.resetZoom();
    this.showItem(this.currentIndex);
    this.updateCounter();
    this.updateActiveThumbnail();
  }

  close() {
    this._clearMediaContent();
    this.isOpen = false;
    this.resetZoom();
    this.resetAdjustments({ hard: true });
    this.setAdjustCollapsed(true);
    this.overlay.classList.remove('lbt-open');
    document.body.style.overflow = '';
  }

  prev() {
    const total = this.items.length;
    if (total <= 1) return;
    this.resetZoom();
    this.currentIndex = (this.currentIndex - 1 + total) % total;
    this.showItem(this.currentIndex);
    this.updateCounter();
    this.updateActiveThumbnail();
  }

  next() {
    const total = this.items.length;
    if (total <= 1) return;
    this.resetZoom();
    this.currentIndex = (this.currentIndex + 1) % total;
    this.showItem(this.currentIndex);
    this.updateCounter();
    this.updateActiveThumbnail();
  }

  goTo(index) {
    if (index === this.currentIndex) return;
    this.resetZoom();
    this.currentIndex = index;
    this.showItem(this.currentIndex);
    this.updateCounter();
    this.updateActiveThumbnail();
  }

  updateCounter() {
    if (!this.counter) return;
    this.counter.textContent = `${this.currentIndex + 1} / ${this.items.length}`;
  }

  updateActiveThumbnail() {
    const update = (list) => {
      if (!list) return;
      list.forEach((t, i) => {
        t.classList.toggle('lbt-thumb-active', i === this.currentIndex);
      });
      const active = list[this.currentIndex];
      if (active) active.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    };
    update(this.thumbs);
    update(this.mobileThumbsList);
  }

  bindAdjustEvents() {
    if (!this.adjustControls) return;

    this.adjustBrightnessInput = this.adjustPanel?.querySelector('.lbt-adjust-brightness');
    this.adjustContrastInput = this.adjustPanel?.querySelector('.lbt-adjust-contrast');
    this.adjustSaturationInput = this.adjustPanel?.querySelector('.lbt-adjust-saturation');
    this.adjustWarmthInput = this.adjustPanel?.querySelector('.lbt-adjust-warmth');
    this.adjustSharpnessInput = this.adjustPanel?.querySelector('.lbt-adjust-sharpness');
    this.adjustBrightnessValue = this.adjustPanel?.querySelector('.lbt-adjust-brightness-value');
    this.adjustContrastValue = this.adjustPanel?.querySelector('.lbt-adjust-contrast-value');
    this.adjustSaturationValue = this.adjustPanel?.querySelector('.lbt-adjust-saturation-value');
    this.adjustWarmthValue = this.adjustPanel?.querySelector('.lbt-adjust-warmth-value');
    this.adjustSharpnessValue = this.adjustPanel?.querySelector('.lbt-adjust-sharpness-value');
    this.adjustPresetButtons = this.adjustPanel?.querySelectorAll('.lbt-adjust-preset:not(.lbt-adjust-scene)');
    this.adjustSceneButtons = this.adjustPanel?.querySelectorAll('.lbt-adjust-scene');
    this.adjustResetButton = this.adjustPanel?.querySelector('.lbt-adjust-reset');
    this.adjustGrayscaleBtn = this.adjustPanel?.querySelector('.lbt-adjust-grayscale-btn');
    this.adjustInvertBtn = this.adjustPanel?.querySelector('.lbt-adjust-invert-btn');
    this.adjustPipetteBtn = this.adjustPanel?.querySelector('.lbt-adjust-pipette-btn');
    this.adjustSplitViewBtn = this.adjustPanel?.querySelector('.lbt-adjust-splitview-btn');
    this._pipetteCanvas = this.viewport?.querySelector('.lbt-pipette-canvas');
    this._pipetteTooltip = this.viewport?.querySelector('.lbt-pipette-tooltip');
    this._splitViewHandle = this.viewport?.querySelector('.lbt-splitview-handle');
    this._splitViewOriginal = this.viewport?.querySelector('.lbt-splitview-original');

    this.adjustToggleButton?.addEventListener('click', () => {
      this.setAdjustCollapsed(!this.adjustCollapsed);
    });
    this.adjustPanelCloseButton?.addEventListener('click', () => {
      this.setAdjustCollapsed(true);
    });

    this.adjustControls?.addEventListener('click', (event) => {
      event.stopPropagation();
    });

    const onInput = () => {
      this.adjust.brightness = parseInt(this.adjustBrightnessInput?.value || '100', 10);
      this.adjust.contrast = parseInt(this.adjustContrastInput?.value || '100', 10);
      this.adjust.saturation = parseInt(this.adjustSaturationInput?.value || '100', 10);
      this.adjust.warmth = parseInt(this.adjustWarmthInput?.value || '0', 10);
      this.adjust.sharpness = parseInt(this.adjustSharpnessInput?.value || '0', 10);
      this.adjust.preset = 'custom';
      this.updateAdjustUI();
      this.applyAdjustments();
    };

    this.adjustBrightnessInput?.addEventListener('input', onInput);
    this.adjustContrastInput?.addEventListener('input', onInput);
    this.adjustSaturationInput?.addEventListener('input', onInput);
    this.adjustWarmthInput?.addEventListener('input', onInput);
    this.adjustSharpnessInput?.addEventListener('input', onInput);

    this.adjustPresetButtons?.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.setPreset(btn.dataset.preset || 'neutral');
      });
    });

    this.adjustSceneButtons?.forEach((btn) => {
      btn.addEventListener('click', () => {
        this.setScene(btn.dataset.scene);
      });
    });

    this.adjustGrayscaleBtn?.addEventListener('click', () => {
      this.adjust.grayscale = !this.adjust.grayscale;
      this.adjust.preset = 'custom';
      this.updateAdjustUI();
      this.applyAdjustments();
    });

    this.adjustInvertBtn?.addEventListener('click', () => {
      this.adjust.invert = !this.adjust.invert;
      this.adjust.preset = 'custom';
      this.updateAdjustUI();
      this.applyAdjustments();
    });

    this.adjustPipetteBtn?.addEventListener('click', () => {
      this._togglePipette();
    });

    this.adjustSplitViewBtn?.addEventListener('click', () => {
      this._toggleSplitView();
    });

    this.adjustResetButton?.addEventListener('click', () => {
      this.resetAdjustments({ hard: false });
    });
  }

  toggleAdjustControlsForMedia(item) {
    if (!this.enableColorAdjustments) return;
    const isImage = this._isImageType(item);
    if (this.adjustToggleButton) this.adjustToggleButton.style.display = isImage ? '' : 'none';
    if (!isImage) this.setAdjustCollapsed(true);
  }

  setAdjustCollapsed(collapsed) {
    if (!this.adjustPanel || !this.adjustToggleButton) return;
    this.adjustCollapsed = !!collapsed;
    this.adjustPanel.style.display = this.adjustCollapsed ? 'none' : 'flex';
    this.adjustToggleButton.setAttribute('aria-expanded', String(!this.adjustCollapsed));
    this.adjustToggleButton.classList.toggle('is-active', !this.adjustCollapsed);
    if (this.adjustToggleLabel) {
      this.adjustToggleLabel.textContent = this.adjustCollapsed
        ? this.adjustI18n.toggle
        : this.adjustI18n.close;
    }
  }

  setPreset(name) {
    if (!this.enableColorAdjustments) return;
    const preset = this.adjustPresets[name] || this.adjustPresets.neutral;
    this.adjust = {
      ...this.adjust,
      brightness: preset.brightness,
      contrast: preset.contrast,
      saturation: preset.saturation,
      warmth: preset.warmth ?? 0,
      sharpness: preset.sharpness ?? 0,
      preset: name
    };
    this._activeScene = null;
    this.updateAdjustUI();
    this.applyAdjustments();
  }

  setScene(name) {
    if (!this.enableColorAdjustments) return;
    const scene = this.scenePresets[name];
    if (!scene) return;
    this.adjust = {
      ...this.adjust,
      brightness: scene.brightness,
      contrast: scene.contrast,
      saturation: scene.saturation,
      warmth: scene.warmth ?? 0,
      sharpness: scene.sharpness ?? 0,
      preset: 'custom'
    };
    this._activeScene = name;
    this.updateAdjustUI();
    this.applyAdjustments();
  }

  resetAdjustments({ hard = false } = {}) {
    if (!this.enableColorAdjustments) return;
    this.adjust = {
      brightness: 100,
      contrast: 100,
      saturation: 100,
      warmth: 0,
      sharpness: 0,
      grayscale: false,
      invert: false,
      preset: 'neutral'
    };
    this._activeScene = null;
    if (this._pipetteActive) this._togglePipette();
    if (this._splitViewActive) this._toggleSplitView();
    this.updateAdjustUI();
    this.applyAdjustments();
    if (hard && this.adjustControls) {
      this.adjustControls.style.display = '';
    }
  }

  updateAdjustUI() {
    if (!this.enableColorAdjustments || !this.adjustControls) return;
    if (this.adjustBrightnessInput) this.adjustBrightnessInput.value = String(this.adjust.brightness);
    if (this.adjustContrastInput) this.adjustContrastInput.value = String(this.adjust.contrast);
    if (this.adjustSaturationInput) this.adjustSaturationInput.value = String(this.adjust.saturation);
    if (this.adjustWarmthInput) this.adjustWarmthInput.value = String(this.adjust.warmth);
    if (this.adjustSharpnessInput) this.adjustSharpnessInput.value = String(this.adjust.sharpness);
    if (this.adjustBrightnessValue) this.adjustBrightnessValue.textContent = `${this.adjust.brightness}%`;
    if (this.adjustContrastValue) this.adjustContrastValue.textContent = `${this.adjust.contrast}%`;
    if (this.adjustSaturationValue) this.adjustSaturationValue.textContent = `${this.adjust.saturation}%`;
    if (this.adjustWarmthValue) this.adjustWarmthValue.textContent = String(this.adjust.warmth);
    if (this.adjustSharpnessValue) this.adjustSharpnessValue.textContent = String(this.adjust.sharpness);
    this.adjustPresetButtons?.forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.preset === this.adjust.preset);
    });
    this.adjustSceneButtons?.forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.scene === this._activeScene);
    });
    this.adjustGrayscaleBtn?.classList.toggle('is-active', !!this.adjust.grayscale);
    this.adjustInvertBtn?.classList.toggle('is-active', !!this.adjust.invert);
    this.adjustPipetteBtn?.classList.toggle('is-active', !!this._pipetteActive);
    this.adjustSplitViewBtn?.classList.toggle('is-active', !!this._splitViewActive);
  }

  applyAdjustments() {
    if (!this.mainImg) return;
    if (!this.enableColorAdjustments || !this._isImageType(this.items[this.currentIndex])) {
      this.mainImg.style.filter = '';
      return;
    }
    const f = [];
    f.push(`brightness(${this.adjust.brightness}%)`);
    f.push(`contrast(${this.adjust.contrast}%)`);
    f.push(`saturate(${this.adjust.saturation}%)`);
    // Warmth: positive = warm (sepia + slight hue shift), negative = cool (hue shift only)
    const w = this.adjust.warmth || 0;
    if (w > 0) {
      f.push(`sepia(${Math.min(w * 1.5, 60)}%)`);
      f.push(`hue-rotate(${Math.round(w * -0.3)}deg)`);
    } else if (w < 0) {
      f.push(`sepia(${Math.min(Math.abs(w) * 0.5, 15)}%)`);
      f.push(`hue-rotate(${Math.round(w * 1.2)}deg)`);
    }
    // Sharpness: micro-contrast boost
    const s = this.adjust.sharpness || 0;
    if (s > 0) {
      f.push(`contrast(${100 + s * 1.5}%)`);
    }
    if (this.adjust.grayscale) f.push('grayscale(1)');
    if (this.adjust.invert) f.push('invert(1)');
    this.mainImg.style.filter = f.join(' ');
  }

  // === Pipette (Color Picker) ===
  _togglePipette() {
    this._pipetteActive = !this._pipetteActive;
    this.updateAdjustUI();
    if (this._pipetteActive) {
      if (this._splitViewActive) this._toggleSplitView();
      this.viewport?.classList.add('lbt-pipette-mode');
      this._pipetteHandler = (e) => this._onPipetteClick(e);
      this._pipetteMoveHandler = (e) => this._onPipetteMove(e);
      this.viewport?.addEventListener('click', this._pipetteHandler);
      this.viewport?.addEventListener('mousemove', this._pipetteMoveHandler);
      this.viewport?.addEventListener('touchmove', this._pipetteMoveHandler, { passive: true });
    } else {
      this.viewport?.classList.remove('lbt-pipette-mode');
      this.viewport?.removeEventListener('click', this._pipetteHandler);
      this.viewport?.removeEventListener('mousemove', this._pipetteMoveHandler);
      this.viewport?.removeEventListener('touchmove', this._pipetteMoveHandler);
      if (this._pipetteTooltip) this._pipetteTooltip.style.display = 'none';
    }
  }

  _getPipetteColor(clientX, clientY) {
    if (!this.mainImg || !this._pipetteCanvas) return null;
    const canvas = this._pipetteCanvas;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    // Draw current image to canvas (without CSS filters — we want the raw pixel)
    const img = this.mainImg;
    if (!img.naturalWidth) return null;
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    ctx.drawImage(img, 0, 0, img.naturalWidth, img.naturalHeight);
    // Map click coords to image pixel coords
    const rect = img.getBoundingClientRect();
    const scaleX = img.naturalWidth / rect.width;
    const scaleY = img.naturalHeight / rect.height;
    const px = Math.round((clientX - rect.left) * scaleX);
    const py = Math.round((clientY - rect.top) * scaleY);
    if (px < 0 || py < 0 || px >= img.naturalWidth || py >= img.naturalHeight) return null;
    const data = ctx.getImageData(px, py, 1, 1).data;
    const r = data[0], g = data[1], b = data[2];
    const hex = '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
    return { r, g, b, hex };
  }

  _onPipetteMove(e) {
    if (!this._pipetteActive || !this._pipetteTooltip) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;
    const color = this._getPipetteColor(cx, cy);
    if (!color) { this._pipetteTooltip.style.display = 'none'; return; }
    this._pipetteTooltip.style.display = 'flex';
    this._pipetteTooltip.innerHTML = `<span class="lbt-pipette-swatch" style="background:${color.hex}"></span><span class="lbt-pipette-hex">${color.hex}</span><span class="lbt-pipette-rgb">rgb(${color.r},${color.g},${color.b})</span>`;
    const vRect = this.viewport.getBoundingClientRect();
    let tx = cx - vRect.left + 16;
    let ty = cy - vRect.top - 40;
    if (tx + 180 > vRect.width) tx = cx - vRect.left - 180;
    if (ty < 0) ty = cy - vRect.top + 20;
    this._pipetteTooltip.style.left = tx + 'px';
    this._pipetteTooltip.style.top = ty + 'px';
  }

  _onPipetteClick(e) {
    if (!this._pipetteActive) return;
    e.preventDefault();
    e.stopPropagation();
    const color = this._getPipetteColor(e.clientX, e.clientY);
    if (!color) return;
    // Copy to clipboard
    if (navigator.clipboard) {
      navigator.clipboard.writeText(color.hex).catch(() => { if (window.theme && window.theme.fallbackCopy) window.theme.fallbackCopy(color.hex); });
    } else if (window.theme && window.theme.fallbackCopy) {
      window.theme.fallbackCopy(color.hex);
    }
    // Flash the tooltip
    if (this._pipetteTooltip) {
      this._pipetteTooltip.classList.add('lbt-pipette-copied');
      this._pipetteTooltip.innerHTML += `<span class="lbt-pipette-copied-label">${window.__lbtCopiedLabel || 'Copied!'}</span>`;
      setTimeout(() => this._pipetteTooltip?.classList.remove('lbt-pipette-copied'), 1200);
    }
  }

  // === Split View (Before / After) ===
  // Left = Original (unfiltered), Right = Filtered (with CSS filters on mainImg)
  // The original img is placed inside imgWrap so it follows pan/zoom transforms automatically.
  _toggleSplitView() {
    this._splitViewActive = !this._splitViewActive;
    this.updateAdjustUI();
    if (this._splitViewActive) {
      if (this._pipetteActive) this._togglePipette();
      this._splitPosition = 50;
      const item = this.items[this.currentIndex];
      if (!item || item.type !== 'image') { this._splitViewActive = false; return; }
      // Move original img into imgWrap (sibling of mainImg) so it follows transforms
      if (this._splitViewOriginal && this.imgWrap) {
        this._splitViewOriginal.src = item.src || '';
        this._splitViewOriginal.style.display = 'block';
        this._splitViewOriginal.style.filter = 'none';
        this.imgWrap.appendChild(this._splitViewOriginal);
      }
      if (this._splitViewHandle) this._splitViewHandle.style.display = 'flex';
      this.viewport?.classList.add('lbt-splitview-mode');
      this._applySplitPosition();
      // Bind drag events for the handle
      this._splitDragHandler = (e) => this._onSplitDrag(e);
      this._splitDragEndHandler = () => this._onSplitDragEnd();
      this._splitMouseDown = (e) => { e.preventDefault(); e.stopPropagation(); this._splitDragging = true; };
      this._splitTouchStart = (e) => { e.preventDefault(); e.stopPropagation(); this._splitDragging = true; };
      this._splitViewHandle?.addEventListener('mousedown', this._splitMouseDown);
      this._splitViewHandle?.addEventListener('touchstart', this._splitTouchStart, { passive: false });
      window.addEventListener('mousemove', this._splitDragHandler);
      window.addEventListener('touchmove', this._splitDragHandler, { passive: true });
      window.addEventListener('mouseup', this._splitDragEndHandler);
      window.addEventListener('touchend', this._splitDragEndHandler);
    } else {
      this.viewport?.classList.remove('lbt-splitview-mode');
      // Move original img back to viewport (out of imgWrap)
      if (this._splitViewOriginal && this.viewport) {
        this.viewport.appendChild(this._splitViewOriginal);
        this._splitViewOriginal.style.display = 'none';
        this._splitViewOriginal.style.clipPath = '';
        this._splitViewOriginal.style.width = '';
        this._splitViewOriginal.style.height = '';
        this._splitViewOriginal.style.position = '';
      }
      if (this._splitViewHandle) this._splitViewHandle.style.display = 'none';
      if (this.mainImg) this.mainImg.style.clipPath = '';
      this._splitViewHandle?.removeEventListener('mousedown', this._splitMouseDown);
      this._splitViewHandle?.removeEventListener('touchstart', this._splitTouchStart);
      window.removeEventListener('mousemove', this._splitDragHandler);
      window.removeEventListener('touchmove', this._splitDragHandler);
      window.removeEventListener('mouseup', this._splitDragEndHandler);
      window.removeEventListener('touchend', this._splitDragEndHandler);
      this._splitDragging = false;
    }
  }

  _applySplitPosition() {
    const p = this._splitPosition;
    if (!this.mainImg) return;
    // The original img is inside imgWrap — position it exactly over mainImg
    if (this._splitViewOriginal) {
      // mainImg is centered by flexbox inside imgWrap — use offsetLeft/offsetTop to find its position
      this._splitViewOriginal.style.position = 'absolute';
      this._splitViewOriginal.style.left = this.mainImg.offsetLeft + 'px';
      this._splitViewOriginal.style.top = this.mainImg.offsetTop + 'px';
      this._splitViewOriginal.style.width = this.mainImg.offsetWidth + 'px';
      this._splitViewOriginal.style.height = this.mainImg.offsetHeight + 'px';
      this._splitViewOriginal.style.maxWidth = 'none';
      this._splitViewOriginal.style.maxHeight = 'none';
      this._splitViewOriginal.style.objectFit = 'contain';
      // Clip: show only the LEFT portion (original/unfiltered)
      this._splitViewOriginal.style.clipPath = `inset(0 ${100 - p}% 0 0)`;
    }
    // mainImg (filtered) is fully visible — original covers the left, so filtered shows on right
    this.mainImg.style.clipPath = '';
    // Position the drag handle relative to the visible image on screen
    if (this._splitViewHandle && this.viewport) {
      const imgRect = this.mainImg.getBoundingClientRect();
      const vpRect = this.viewport.getBoundingClientRect();
      const imgLeft = imgRect.left - vpRect.left;
      const handlePx = imgLeft + (imgRect.width * p / 100);
      this._splitViewHandle.style.left = handlePx + 'px';
    }
  }

  _onSplitDrag(e) {
    if (!this._splitDragging || !this._splitViewActive) return;
    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const imgRect = this.mainImg?.getBoundingClientRect();
    if (!imgRect) return;
    this._splitPosition = Math.max(5, Math.min(95, ((cx - imgRect.left) / imgRect.width) * 100));
    this._applySplitPosition();
  }

  _onSplitDragEnd() {
    this._splitDragging = false;
  }

  // === Zoom ===
  getFitWidthScale() {
    // Calculate scale to fill viewport width, capped at 100% native size
    const rect = this.viewport?.getBoundingClientRect();
    if (!rect) return 1.5;
    const img = this.items[this.currentIndex];
    if (!img) return 1.5;
    const { w } = this.getDisplayedSize();
    if (w <= 0) return 1.5;
    // Scale needed to fill viewport width from displayed size at 1x
    const needed = rect.width / w;
    // Cap so we never exceed native 100% (img.width pixels displayed)
    const maxForNative = img.width / w;
    return Math.min(needed, maxForNative, this.maxScale);
  }

  zoomToFitWidth() {
    const target = this.getFitWidthScale();
    this.scale = target;
    this.panX = 0;
    this.panY = 0;
    this.clampPan();
    this.applyTransform();
    this.updateZoomUI();
  }

  zoomBy(delta, cx, cy) {
    const oldScale = this.scale;
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, this.scale + delta));
    if (Math.abs(this.scale - oldScale) < 0.001) return;

    if (cx !== undefined && cy !== undefined) {
      const rect = this.viewport.getBoundingClientRect();
      const ox = cx - rect.left - rect.width / 2;
      const oy = cy - rect.top - rect.height / 2;
      this.panX = this.panX - ox * (this.scale / oldScale - 1);
      this.panY = this.panY - oy * (this.scale / oldScale - 1);
    }

    this.clampPan();
    this.applyTransform();
    this.updateZoomUI();
  }

  setScale(newScale) {
    this.scale = Math.min(this.maxScale, Math.max(this.minScale, newScale));
    this.clampPan();
    this.applyTransform();
    this.updateZoomUI();
  }

  zoomIn() { this.zoomBy(0.3); }
  zoomOut() { this.zoomBy(-0.3); }

  resetZoom() {
    this.scale = 1;
    this.panX = 0;
    this.panY = 0;
    // Reset img to CSS-controlled sizing
    if (this.mainImg) {
      this.mainImg.style.width = '';
      this.mainImg.style.height = '';
      this.mainImg.style.maxWidth = '';
      this.mainImg.style.maxHeight = '';
    }
    if (this._splitViewOriginal) {
      this._splitViewOriginal.style.width = '';
      this._splitViewOriginal.style.height = '';
    }
    this.applyTransform();
    this.updateZoomUI();
  }

  getDisplayedSize() {
    // Calculate the actual displayed image size at scale=1
    const rect = this.viewport?.getBoundingClientRect();
    if (!rect) return { w: 0, h: 0 };
    const img = this.items[this.currentIndex];
    if (!img) return { w: 0, h: 0 };
    const ratioW = rect.width / img.width;
    const ratioH = rect.height / img.height;
    const fitRatio = Math.min(ratioW, ratioH, 1);
    return { w: img.width * fitRatio, h: img.height * fitRatio };
  }

  clampPan() {
    if (this.scale <= 1) {
      this.panX = 0;
      this.panY = 0;
      return;
    }
    const rect = this.viewport?.getBoundingClientRect();
    if (!rect) return;
    const { w, h } = this.getDisplayedSize();
    const scaledW = w * this.scale;
    const scaledH = h * this.scale;
    const maxPanX = Math.max(0, (scaledW - rect.width) / 2);
    const maxPanY = Math.max(0, (scaledH - rect.height) / 2);
    this.panX = Math.max(-maxPanX, Math.min(maxPanX, this.panX));
    this.panY = Math.max(-maxPanY, Math.min(maxPanY, this.panY));
  }

  applyTransform() {
    if (!this.imgWrap) return;
    if (this.mainImg) {
      if (this.scale > 1) {
        // Zoomed: set explicit width/height so browser rasterizes at full resolution (sharp on Retina/mobile)
        const { w, h } = this.getDisplayedSize();
        if (w > 0 && h > 0) {
          this.mainImg.style.width = `${w * this.scale}px`;
          this.mainImg.style.height = `${h * this.scale}px`;
          this.mainImg.style.maxWidth = 'none';
          this.mainImg.style.maxHeight = 'none';
        }
      } else {
        // At 1x: let CSS handle sizing (max-width/max-height: 100%)
        this.mainImg.style.width = '';
        this.mainImg.style.height = '';
        this.mainImg.style.maxWidth = '';
        this.mainImg.style.maxHeight = '';
      }
    }
    this.imgWrap.style.transform = `translate(${this.panX}px, ${this.panY}px)`;
    // Sync split-view original image with main image when active
    if (this._splitViewActive) this._applySplitPosition();
    // Update slider and label
    if (this.zoomSlider) this.zoomSlider.value = Math.round(this.scale * 100);
    if (this.zoomLabel) this.zoomLabel.textContent = Math.round(this.scale * 100) + '%';
  }

  updateZoomUI() {
    if (!this.viewport) return;
    this.viewport.classList.toggle('lbt-zoomed', this.scale > 1);
  }

  // === Touch handling ===
  getTouchDist(e) {
    const t = e.touches;
    const dx = t[0].clientX - t[1].clientX;
    const dy = t[0].clientY - t[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  getTouchCenter(e) {
    const t = e.touches;
    return {
      x: (t[0].clientX + t[1].clientX) / 2,
      y: (t[0].clientY + t[1].clientY) / 2
    };
  }

  onTouchStart(e) {
    if (!this.isOpen) return;
    if (this._pipetteActive) return;

    if (e.touches.length === 2) {
      e.preventDefault();
      this.isPinching = true;
      this.isTouching = false;
      this.pinchStartDist = this.getTouchDist(e);
      this.pinchStartScale = this.scale;
      return;
    }

    if (e.touches.length === 1) {
      this.isTouching = true;
      this.touchAxis = null;
      this.touchStartX = e.touches[0].clientX;
      this.touchStartY = e.touches[0].clientY;
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.mouseDragBaseX = this.panX;
      this.mouseDragBaseY = this.panY;
      // Double-tap detection
      const now = Date.now();
      if (this._lastTapTime && now - this._lastTapTime < 300) {
        this._lastTapTime = 0;
        this._doubleTapped = true;
        if (this.scale > 1) {
          this.resetZoom();
        } else {
          this.zoomToFitWidth();
        }
        return;
      }
      this._lastTapTime = now;
      this._doubleTapped = false;
    }
  }

  onTouchMove(e) {
    if (!this.isOpen) return;

    if (this.isPinching && e.touches.length === 2) {
      e.preventDefault();
      const dist = this.getTouchDist(e);
      const newScale = Math.min(this.maxScale, Math.max(this.minScale, this.pinchStartScale * (dist / this.pinchStartDist)));
      const center = this.getTouchCenter(e);
      const rect = this.viewport.getBoundingClientRect();
      const ox = center.x - rect.left - rect.width / 2;
      const oy = center.y - rect.top - rect.height / 2;
      if (this.scale !== newScale) {
        const ratio = newScale / this.scale;
        this.panX = this.panX - ox * (ratio - 1);
        this.panY = this.panY - oy * (ratio - 1);
        this.scale = newScale;
        this.clampPan();
        this.applyTransform();
        this.updateZoomUI();
      }
      return;
    }

    if (this.isTouching && e.touches.length === 1 && !this._doubleTapped) {
      const dx = e.touches[0].clientX - this.touchStartX;
      const dy = e.touches[0].clientY - this.touchStartY;
      if (this.scale > 1) {
        // When zoomed: pan in ALL directions with finger
        e.preventDefault();
        this.panX = this.mouseDragBaseX + dx;
        this.panY = this.mouseDragBaseY + dy;
        this.clampPan();
        this.applyTransform();
        this.touchDeltaX = dx;
      } else {
        // At 1x: horizontal swipe navigates
        if (!this.touchAxis && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          this.touchAxis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
        }
        if (this.touchAxis === 'x') {
          e.preventDefault();
          this.touchDeltaX = dx;
        }
      }
    }
  }

  onTouchEnd() {
    if (this.isPinching) {
      this.isPinching = false;
      if (this.scale <= 1.05) this.resetZoom();
      return;
    }
    if (this.isTouching) {
      this.isTouching = false;
      if (this._doubleTapped) { this._doubleTapped = false; return; }
      if (this.scale <= 1 && this.touchAxis === 'x') {
        // Navigate only at 1x
        if (this.touchDeltaX < -50) this.next();
        else if (this.touchDeltaX > 50) this.prev();
      }
      this.touchDeltaX = 0;
      this.touchDeltaY = 0;
      this.touchAxis = null;
    }
  }

  onTouchCancel() {
    this.isPinching = false;
    this.isTouching = false;
    this.isMouseDragging = false;
  }

  destroy() {
    if (this.overlay) {
      this.close();
      this.overlay.remove();
      this.overlay = null;
      this.built = false;
    }
  }
}

window.LightboxThumbsV2 = LightboxThumbsV2;
